import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "../../styles/newDesign.scss";
import "./Home.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

// Helper
import { apiFetch, resolveTenant, listCourseCatalog } from "../../Helper/Api";

// Properties
const properties = require("../../properties.json");

/** First free lesson that actually has a playable source, in priority order: a direct uploaded
 *  file (plays inline, no embed needed) beats a YouTube/Vimeo link (iframe embed) -- see
 *  LessonResponse.videoLinks/youtubeLinks/vimeoLinks. Lessons with none of the three (e.g. only
 *  images) are skipped rather than treated as "no intro video available". */
function findIntroVideo(lessons) {
  for (const lesson of lessons) {
    if (lesson.videoLinks && lesson.videoLinks[0]) {
      return { type: "file", src: lesson.videoLinks[0], title: lesson.lessonName };
    }
    if (lesson.youtubeLinks && lesson.youtubeLinks[0]) {
      const id = lesson.youtubeLinks[0].split("/").pop().replace("watch?v=", "");
      return { type: "embed", src: `https://www.youtube.com/embed/${id}?autoplay=1`, title: lesson.lessonName };
    }
    if (lesson.vimeoLinks && lesson.vimeoLinks[0]) {
      const id = lesson.vimeoLinks[0].split("/").pop();
      return { type: "embed", src: `https://player.vimeo.com/video/${id}?autoplay=1`, title: lesson.lessonName };
    }
  }
  return null;
}

class Home extends Component {
  state = {
    tenant: null,
    notFound: false,
    lessons: [],
    courses: [],
    introOpen: false,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${tenant.name} | ${properties.site_name}` : properties.site_name;
        this.setState({ tenant });
        return Promise.all([
          apiFetch("/lessons/free", { query: { teacherUserId: tenant.teacherUserId } }),
          listCourseCatalog(tenant.teacherUserId),
        ]);
      })
      .then(([lessons, courses]) => this.setState({ lessons: lessons || [], courses: courses || [] }))
      .catch(() => this.setState({ notFound: true }));
  }

  openIntro = () => this.setState({ introOpen: true });
  closeIntro = () => this.setState({ introOpen: false });

  render() {
    const { tenant, notFound, lessons, courses, introOpen } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>No page was found for this domain.</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name;
    const intro = findIntroVideo(lessons);
    const paths = courses.filter((c) => c.showOnHome);

    return (
      <>
        <Header tenant={tenant} />

        <div className="nd-page">
          <div className="home-hero">
            <div className="home-hero-copy">
              <div className="nd-eyebrow">From Sketch to Reality</div>
              <h1>Learn Online with<br />{teacherName}</h1>
              <p>Hands-on courses in design and modeling. Learn step by step and build real projects from the first idea to the final result.</p>
              <div className="home-hero-actions">
                <Link to="/courses" className="nd-btn-dark">Explore Courses →</Link>
                {intro && (
                  <button type="button" className="home-watch" onClick={this.openIntro}>
                    <span className="home-watch-circle">▶</span> Watch Intro
                  </button>
                )}
              </div>
              <div className="home-hero-auth">
                <Link to="/sign-up" className="nd-btn-ghost">
                  <i className="fas fa-user-plus"></i> Create Account
                </Link>
                <Link to="/sign-in" className="nd-btn-ghost">
                  <i className="fas fa-sign-in-alt"></i> Login
                </Link>
              </div>
            </div>
            <div
              className="home-hero-visual"
              style={{ backgroundImage: "url(/assets/hero-sketch-to-render.webp)", backgroundSize: "cover", backgroundPosition: "center" }}
            >
              <div className="home-hero-visual-label">Concept sketch → aerial resort render</div>
            </div>
          </div>

          {paths.length > 0 && (
            <div className="nd-section nd-wrap">
              <div className="nd-eyebrow">Learning Paths</div>
              <h2 className="home-section-title">Choose Your Path</h2>
              <div className="home-path-grid">
                {paths.map((course, i) => (
                  <a href={`/courses/${course.id}`} className="home-path-card" key={course.id}>
                    <div
                      className="home-path-thumb"
                      style={course.image ? { backgroundImage: `url(${course.image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                    >
                      <div className="home-path-num">{String(i + 1).padStart(2, "0")}</div>
                    </div>
                    <div className="home-path-body">
                      <div className="home-path-title-row"><h3>{course.title}</h3><span>→</span></div>
                      {course.description && <p>{course.description}</p>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="nd-section nd-wrap home-workflow">
            <div
              className="home-workflow-visual"
              style={{ backgroundImage: "url(/assets/learn-from-real-workflow.png)", backgroundSize: "cover", backgroundPosition: "center" }}
            ></div>
            <div className="home-workflow-copy">
              <div className="nd-eyebrow">Real Projects</div>
              <h2>Learn From Real Workflows</h2>
              <p>Follow a clear, step-by-step process from concept sketches to final render. See how architectural ideas come to life using Rhino 3D.</p>
              <div className="home-flow">
                <span className="home-flow-script">Sketch</span><span className="home-flow-arrow">→</span>
                <span className="home-flow-script">Rhino</span><span className="home-flow-arrow">→</span>
                <span className="home-flow-script">Render</span>
              </div>
            </div>
          </div>

          {tenant.description && (
            <div className="nd-section nd-wrap home-about">
              <div className="home-about-text">
                <div className="nd-eyebrow">About Me</div>
                <h2>About {teacherName}</h2>
                <p>{tenant.description}</p>
              </div>
              <div className="home-about-media"></div>
            </div>
          )}

        </div>

        {intro && (
          <div className={`nd-page home-intro-overlay ${introOpen ? "open" : ""}`} onClick={(e) => e.target === e.currentTarget && this.closeIntro()}>
            {introOpen && (
              <div className="home-intro-box">
                <button className="home-intro-close" onClick={this.closeIntro}>✕</button>
                {intro.type === "file" ? (
                  <video src={intro.src} controls autoPlay style={{ width: "100%", display: "block", borderRadius: 14 }} />
                ) : (
                  <iframe
                    src={intro.src}
                    title={intro.title}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    style={{ width: "100%", aspectRatio: "16/9", border: "none", borderRadius: 14, display: "block" }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Home;
